import { FC } from 'react';
import { Listbox } from '@headlessui/react';
import { Controller, UseFormSetValue } from 'react-hook-form';
import { ReactComponent as DropdownIcon } from 'assets/icons/chevron-down.svg';
import { ReactComponent as CloseIcon } from 'assets/icons/add_circle_outline.svg';
import { ReactComponent as InfoIcon } from 'assets/icons/info.svg';
import ReactTooltip from 'react-tooltip';
import { isNumber } from 'lodash';
import { CreateOrEditServiceSubmitData } from '../CreateOrEditService';

interface PeriodYearOptionProps {
  control: any;
  setValue: UseFormSetValue<CreateOrEditServiceSubmitData>;
  defaultValue?: number | null;
}

const PERIOD_YEAR_OPTIONS = [2, 3, 4, 5].map((year) => ({
  label: `${year} years`,
  value: year,
}));

const PeriodYearOption: FC<PeriodYearOptionProps> = ({ control, setValue }) => (
  <Controller
    name="periodNewPatientYear"
    control={control}
    rules={{ required: true }}
    render={({ field }) => (
      <div className="flex flex-col gap-y-0.8">
        <div className="flex items-start gap-x-0.8">
          <h3 className="text-darkest-grey text-16 leading-[2.4rem] font-bold">
            New Patient After X Years
          </h3>
          <button data-for="service-period-year" data-tip="">
            <InfoIcon className="w-1.6 h-1.6 all-child:fill-mid-grey" />
          </button>
          <ReactTooltip
            id="service-period-year"
            effect="solid"
            place="bottom"
            className="!bg-white !rounded-[0.8rem] !w-[18.5rem] !h-[auto] z-[9999] flex justify-center items-center !opacity-100 shadow-elevation-07 after:hidden before:hidden !px-[10px] !py-[4px]"
          >
            <span className="text-11 text-darkest-grey flex items-center font-normal">
              Set the number of years after which a patient is re-established as
              a new patient.
            </span>
          </ReactTooltip>
        </div>
        <Listbox
          {...field}
          onChange={(value: number | null) => {
            setValue('periodNewPatientYear', value, {
              shouldValidate: true,
              shouldDirty: true,
            });
          }}
        >
          {({ open }) => (
            <div className="relative">
              <Listbox.Button
                className={`relative w-full h-[4.8rem] pl-1.6 cursor-pointer rounded-[1rem] bg-white border-[0.5px] border-light-grey text-left shadow-primary hover:border-magenta hover:shadow-input-active outline-none ${
                  open ? 'border-magenta shadow-input-active' : ''
                }`}
              >
                <span
                  className={`block pr-3 ${
                    isNumber(field.value)
                      ? 'text-darkest-grey'
                      : 'text-mid-grey'
                  }`}
                >
                  {isNumber(field.value)
                    ? PERIOD_YEAR_OPTIONS.find(
                        (option) => option.value === field.value
                      )!.label
                    : 'Select re-establishment period'}
                </span>
                <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                  <DropdownIcon className="w-1.4 h-1.4" />
                </span>
                {isNumber(field.value) && (
                  <button
                    className="absolute inset-y-0 right-3 flex items-center pr-1.6"
                    onClick={(e) => {
                      e.stopPropagation();
                      setValue('periodNewPatientYear', null, {
                        shouldValidate: true,
                        shouldDirty: true,
                      });
                    }}
                  >
                    <CloseIcon className="w-1.6 h-1.6 transform rotate-45 all-child:all-child:fill-grey" />
                  </button>
                )}
              </Listbox.Button>
              <Listbox.Options className="absolute mt-0.5 max-h-16 scrollbar w-full overflow-auto rounded-[0.8rem] bg-white py-1.5 text-base shadow-primary flex flex-col gap-y-1.5 z-10">
                {PERIOD_YEAR_OPTIONS.map(({ label, value }) => (
                  <Listbox.Option
                    key={value}
                    className="relative cursor-default select-none pl-1.6"
                    value={value}
                  >
                    {({ selected }) => (
                      <span
                        className={`block truncate cursor-pointer hover:text-magenta ${
                          selected ? 'text-magenta' : ''
                        }`}
                      >
                        {label}
                      </span>
                    )}
                  </Listbox.Option>
                ))}
              </Listbox.Options>
            </div>
          )}
        </Listbox>
      </div>
    )}
  />
);

export default PeriodYearOption;
